import React from 'react';
import { Card, Typography, Tag, Space, Descriptions,Dropdown, Button } from 'antd';
import type { MenuProps } from "antd";
import { GalleryCard, GalleryItem } from './CardGallery';
import { PictureOutlined,EllipsisOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  EditOutlined,
  FolderOpenOutlined } from '@ant-design/icons';


export type Dataset = {
  id: number;
  name: string;
  system_name?: string;
  updatedAt?: string;
};

export async function renameDataset(id: number, newName: string): Promise<Dataset> {
  console.log(`Renaming dataset ${id} to "${newName}"`);
  const res = await fetch(`/api/datasets/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: newName }),
    credentials: "include",
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => "Rename failed");
    throw new Error(msg || "Rename failed");
  }
  return res.json();
}

export interface DatasetRowProps extends GalleryItem {
  name: string;
  description?: string;
  dataset_version_id?: number;
  imageCount?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  status: 'Active' | 'Archived';
  thumbnailUrl?: string;
  version_number?: number;
}

// Helper function to determine status color
const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'green';
    case 'archived':
      return 'gray';
    default:
      return 'default';
  }
};

type DatasetCardProps = GalleryCard<DatasetRowProps> & {
  onAction?: (action: "open" | "train" | "rename" | "delete", item: DatasetRowProps) => void;
  showActions?: boolean; // default true
};

export const DatasetCard: React.FC<DatasetCardProps> = ({ item, onNavigate: _unused, onAction, showActions = true }) => {
  const statusColor = getStatusColor(item.status);

  const menuItems: MenuProps["items"] = [
    // { key: "open",   icon: <FolderOpenOutlined />, label: "Open" },
    { key: "train",  icon: <PlayCircleOutlined />, label: "Train model" },
    { key: "rename", icon: <EditOutlined />,       label: "Rename" },
    // { type: "divider" },
    // { key: "delete", icon: <DeleteOutlined />,     label: "Delete", danger: true },
  ];

  const handleMenuClick: MenuProps["onClick"] = ({ key, domEvent }) => {
    domEvent.stopPropagation();
    domEvent.preventDefault();
    onAction?.(key as any, item);
  };

  return (
    <Card
      hoverable
      className="dataset-card overflow-hidden rounded-lg shadow-md transition-all w-full hover:shadow-lg relative"
      styles={{ body: { padding: "16px", height: "100%" } }}
      style={{ borderTop: `2px solid transparent`, transition: "all 0.3s ease" }}
    >
      {/* Kebab menu - stops propagation so the gallery wrapper does not navigate */}
      {showActions && (
        <div className="absolute top-2 right-2 z-10">
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{ items: menuItems, onClick: handleMenuClick }}
          >
            <Button
              type="text"
              shape="circle"
              aria-label="More actions"
              icon={<EllipsisOutlined />}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>
      )}

      <div className="flex flex-col h-full">
        {/* Thumbnail */}
        <div className="h-32 flex items-center justify-center mb-4 bg-gray-50 rounded p-2">
          {item.thumbnailUrl ? (
            <img
              src={item.thumbnailUrl}
              alt={`${item.name} thumbnail`}
              className="max-h-full max-w-full object-contain"
              // do NOT stopPropagation here so clicking image still navigates via the gallery wrapper
            />
          ) : (
            <PictureOutlined style={{ fontSize: "48px", opacity: 0.5 }} />
          )}
        </div>

        {/* Title + Status */}
        <div className="mb-4 flex justify-between items-center pr-8">
          <Typography.Title level={5} className="m-0" ellipsis={{ tooltip: item.name }}>
            {item.name}
          </Typography.Title>
          <Tag color={statusColor}>{item.status}</Tag>
        </div>

        {/* Key Info */}
        <Space direction="vertical" size="small" className="mb-2">
          <div className="flex justify-between">
            <Typography.Text type="secondary">Images:</Typography.Text>
            <Typography.Text strong>{item.imageCount || 0}</Typography.Text>
          </div>
          <div className="flex justify-between">
            <Typography.Text type="secondary">Organization:</Typography.Text>
            <Typography.Text strong>{item.createdBy}</Typography.Text>
          </div>
        </Space>

        {/* Description */}
        <div className="mb-4">
          <Typography.Paragraph
            type="secondary"
            ellipsis={{ rows: 2, expandable: false, tooltip: item.description }}
          >
            {item.description || "No description provided"}
          </Typography.Paragraph>
        </div>

        {/* Details */}
        <Descriptions
          size="small"
          column={1}
          bordered
          className="mt-2"
          styles={{ label: { fontWeight: "normal" }, content: { textAlign: "right" } }}
        >
          <Descriptions.Item label="Image Count">{item.imageCount || 0}</Descriptions.Item>
          <Descriptions.Item label="Current Version">
            {item.version_number ? `v${item.version_number}` : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit"
            }).format(new Date(item.createdAt))}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit"
            }).format(new Date(item.updatedAt))}
          </Descriptions.Item>
        </Descriptions>
      </div>
    </Card>
  );
};